import {
  attackDamageMatchups,
  unitDef,
  type PlayerId,
  type UnitKind,
} from "@dailywars/core/engine";
import { Crosshair, Route } from "lucide-react";

import { getSpriteUnitIconImage } from "#/game/unitSprites";
import { UNIT_DESCRIPTION } from "#/routes/-codex.data";

import { CoinMoneyIcon } from "./CoinMoneyIcon";
import { gameDockedPanelTitleClassName } from "./GameDockedPanelShell";
import { BuildListScrollContainer, BuildMenuFooter } from "./ProductionBuildMenuShared";
import type { BuildOption } from "./productionBuildMenuTypes";

function formatAttackRange({ minRange, maxRange }: { minRange: number; maxRange: number }): string {
  return minRange === maxRange ? String(minRange) : `${minRange}–${maxRange}`;
}

function UnitDetailHeader({ unitKind, cost }: { unitKind: UnitKind; cost: number }) {
  const def = unitDef(unitKind);
  const rangeLabel = formatAttackRange(def);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-3">
        <p className={gameDockedPanelTitleClassName}>{def.name}</p>
        <div className="flex shrink-0 items-center gap-3 text-sm tabular-nums text-muted-foreground">
          <span className="inline-flex items-center gap-1" aria-label={`Movement ${def.movement}`}>
            <Route className="size-3.5 shrink-0" aria-hidden />
            {def.movement}
          </span>
          <span
            className="inline-flex items-center gap-1"
            aria-label={`Attack range ${rangeLabel}`}
          >
            <Crosshair className="size-3.5 shrink-0" aria-hidden />
            {rangeLabel}
          </span>
          <span className="inline-flex items-center gap-1" aria-label={`Cost ${cost}`}>
            {cost}
            <CoinMoneyIcon size="sm" />
          </span>
        </div>
      </div>
      <p className="text-xs leading-snug text-muted-foreground">{UNIT_DESCRIPTION[unitKind]}</p>
    </div>
  );
}

function DamageMatchupRow({
  damagePercent,
  defenderKind,
  enemyOwner,
}: {
  damagePercent: number;
  defenderKind: UnitKind;
  enemyOwner: PlayerId;
}) {
  const name = unitDef(defenderKind).name;
  const img = getSpriteUnitIconImage(defenderKind, enemyOwner);

  return (
    <div className="flex w-full min-w-0 items-center gap-2 rounded-lg border bg-card px-2.5 py-1.5">
      <span className="w-10 shrink-0 text-sm font-semibold tabular-nums">{damagePercent}%</span>
      <span className="shrink-0 text-sm text-muted-foreground">vs</span>
      <img src={img.src} alt={name} className="size-7 shrink-0 rounded-sm" />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold">{name}</span>
    </div>
  );
}

function UnitDamageMatchupsList({
  attackerKind,
  enemyOwner,
}: {
  attackerKind: UnitKind;
  enemyOwner: PlayerId;
}) {
  const matchups = attackDamageMatchups(attackerKind);

  if (matchups.length === 0) {
    return (
      <p className="rounded-lg border bg-card px-2.5 py-3 text-sm text-muted-foreground">
        Cannot attack enemy units.
      </p>
    );
  }

  return (
    <BuildListScrollContainer rowCount={matchups.length}>
      {matchups.map((matchup) => (
        <DamageMatchupRow
          key={matchup.defenderKind}
          damagePercent={matchup.damagePercent}
          defenderKind={matchup.defenderKind}
          enemyOwner={enemyOwner}
        />
      ))}
    </BuildListScrollContainer>
  );
}

export function ProductionBuildMenuUnitDetail({
  option,
  enemyOwner,
  onBack,
  onCancel,
  onChoose,
}: {
  option: BuildOption;
  enemyOwner: PlayerId;
  onBack: () => void;
  onCancel: () => void;
  onChoose: (kind: UnitKind) => void;
}) {
  const unitName = unitDef(option.kind).name;

  return (
    <>
      <UnitDetailHeader unitKind={option.kind} cost={option.cost} />
      <UnitDamageMatchupsList attackerKind={option.kind} enemyOwner={enemyOwner} />
      <BuildMenuFooter
        onCancel={onCancel}
        onBack={onBack}
        buyOption={option}
        unitName={unitName}
        onChoose={onChoose}
      />
    </>
  );
}
